import React from 'react';
import { cleanProceedingName } from '../../lib/cleanProceedingName';
import { bureauChipLabel } from './proceedingsBureauUtils';

export interface ProceedingCardModel {
  docket_number: string;
  proceeding_name: string;
  status: string;
  processing_status: string;
  updated_at: string;
  created_at: string;
  last_processed_at?: string | null;
  bureau?: string | null;
  has_nrpm?: boolean;
  last_document_count?: number | null;
}

interface ProceedingListCardProps {
  proceeding: ProceedingCardModel;
  nprmUi: 'unknown' | 'has' | 'none';
  bureauNormalized: string | null;
  formatShortDate: (iso: string) => string;
  onCardClick: () => void;
  onSummarizeClick: () => void;
}

export const ProceedingListCard: React.FC<ProceedingListCardProps> = ({
  proceeding,
  nprmUi,
  bureauNormalized,
  formatShortDate,
  onCardClick,
  onSummarizeClick,
}) => {
  const filings = proceeding.last_document_count ?? 0;
  const lastAt = proceeding.last_processed_at || proceeding.updated_at;
  const lastLabel = lastAt ? formatShortDate(lastAt) : '—';

  // Badge reflects the proceeding's lifecycle (status), not the internal
  // ingest flag (processing_status), which stays 'pending' for every docket.
  const statusClass =
    proceeding.status === 'open'
      ? 'bg-emerald-50 text-emerald-800'
      : proceeding.status === 'closed'
        ? 'bg-neutral-100 text-neutral-500'
        : 'bg-amber-50 text-amber-800';

  const statusLabel =
    proceeding.status === 'open'
      ? 'Active'
      : proceeding.status === 'closed'
        ? 'Closed'
        : proceeding.status || 'Pending';

  const bureauShort = bureauNormalized ? bureauChipLabel(bureauNormalized) : null;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onCardClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onCardClick();
        }
      }}
      className="mb-2.5 flex cursor-pointer gap-4 rounded-[10px] border border-cg-border bg-white p-[18px] transition-[border-color,box-shadow] hover:border-neutral-400 hover:shadow-[0_2px_12px_rgba(0,0,0,0.05)] sm:px-5"
    >
      <div className="min-w-0 flex-1">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium tabular-nums tracking-wide text-cg-green">
            {proceeding.docket_number}
          </span>
          {bureauShort && (
            <span
              className="rounded px-1.5 py-0.5 text-[11px] font-normal text-neutral-500 bg-[#f0f0ec]"
              title={proceeding.bureau || undefined}
            >
              {bureauShort}
            </span>
          )}
          <span className={`rounded px-1.5 py-0.5 text-[11px] font-normal ${statusClass}`}>{statusLabel}</span>
          {nprmUi === 'has' && (
            <span className="rounded px-1.5 py-0.5 text-[11px] font-medium bg-violet-50 text-violet-800">
              NPRM
            </span>
          )}
        </div>
        <h3 className="mb-1.5 line-clamp-2 text-sm font-medium tracking-tight text-cg-ink">
          {cleanProceedingName(proceeding.proceeding_name) || 'No title'}
        </h3>
        <div className="mt-3 flex flex-wrap items-center gap-x-3.5 gap-y-1 text-xs font-light text-neutral-400">
          <span>
            <strong className="font-medium text-neutral-600">{filings}</strong> filings
          </span>
          <span>
            Last activity <strong className="font-medium text-neutral-600">{lastLabel}</strong>
          </span>
        </div>
      </div>
      <div className="flex shrink-0 flex-col items-end justify-between self-stretch border-l border-transparent pl-2 sm:pl-3">
        <div className="text-right">
          <div className="text-[22px] font-medium leading-none tracking-tight text-cg-ink">{filings}</div>
          <div className="mb-2 mt-1 text-[11px] font-light text-neutral-400">filings</div>
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onSummarizeClick();
          }}
          className="whitespace-nowrap rounded-md bg-cg-nav px-3 py-1.5 text-xs font-normal text-white transition-colors hover:bg-cg-green"
        >
          Summarize →
        </button>
      </div>
    </div>
  );
};
